import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { Clock, MapPin, Check } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";

export default function PilgrimageDetail() {
  const { id } = useParams();
  const [pkg, setPkg] = useState(null);
  const [form, setForm] = useState({ traveler_name: "", traveler_email: "", traveler_phone: "", travelers: 1 });
  const [open, setOpen] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => { api.get(`/travel/packages/${id}`).then((r) => setPkg(r.data)); }, [id]);

  const checkout = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/booking/checkout", {
        package_id: id,
        travelers: form.travelers,
        traveler_name: form.traveler_name,
        traveler_email: form.traveler_email,
        traveler_phone: form.traveler_phone,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e)); setPaying(false); }
  };

  if (!pkg) return <div className="max-w-5xl mx-auto p-10 text-[#63534E]">Loading…</div>;

  const total = (pkg.price_usd * form.travelers).toFixed(2);

  return (
    <div data-testid="package-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="rounded-3xl overflow-hidden sacred-border relative">
        <img src={pkg.cover_image} alt={pkg.title} className="w-full h-80 lg:h-96 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#4A0E17]/80 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12">
          <div className="flex items-center gap-5 text-[#F3E5AB] text-sm">
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {pkg.duration_days} days</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> Siem Reap, Cambodia</span>
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl text-[#FAF6F0] mt-3 leading-tight">{pkg.title}</h1>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-10 mt-12">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <div className="overline">Overview</div>
            <p className="text-[#1F1412] text-lg leading-relaxed mt-3">{pkg.summary}</p>
          </div>

          <div>
            <div className="overline">Daily Itinerary</div>
            <div className="mt-5 space-y-5">
              {pkg.itinerary?.map((d) => (
                <div key={d.day} data-testid={`itinerary-day-${d.day}`} className="flex gap-5 bg-[#FFFDF9] sacred-border rounded-2xl p-6">
                  <div className="shrink-0 w-14 h-14 rounded-full bg-[#4A0E17] text-[#D4AF37] flex flex-col items-center justify-center font-serif leading-none">
                    <span className="text-[0.6rem] opacity-80">DAY</span>
                    <span className="text-xl">{d.day}</span>
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-[#4A0E17]">{d.title}</h3>
                    <p className="text-[#63534E] mt-2 leading-relaxed">{d.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="overline">Inclusions</div>
            <ul className="mt-5 grid sm:grid-cols-2 gap-3">
              {pkg.inclusions?.map((inc) => (
                <li key={inc} className="flex items-start gap-3 text-[#1F1412]">
                  <Check className="w-5 h-5 text-[#E65100] shrink-0 mt-0.5" /> {inc}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 h-fit bg-[#FFFDF9] sacred-border rounded-3xl p-7">
          <div className="overline">From</div>
          <div className="font-mono text-4xl text-[#E65100] font-semibold mt-1">${pkg.price_usd.toFixed(0)}</div>
          <div className="text-sm text-[#63534E]">per traveler</div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="open-booking-dialog" className="mt-6 w-full bg-[#E65100] hover:bg-[#BF360C] rounded-full h-12 text-base">Book This Yatra</Button>
            </DialogTrigger>
            <DialogContent data-testid="booking-dialog" className="bg-[#FFFDF9] border-[#D4AF37]/40 max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#4A0E17]">Complete your booking</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <Input data-testid="book-traveler-name" placeholder="Full name" value={form.traveler_name} onChange={(e) => setForm({ ...form, traveler_name: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
                <Input data-testid="book-traveler-email" type="email" placeholder="Email" value={form.traveler_email} onChange={(e) => setForm({ ...form, traveler_email: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
                <Input data-testid="book-traveler-phone" placeholder="Phone (optional)" value={form.traveler_phone} onChange={(e) => setForm({ ...form, traveler_phone: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
                <Input data-testid="book-travelers-count" type="number" min="1" max="20" placeholder="Number of travelers" value={form.travelers} onChange={(e) => setForm({ ...form, travelers: parseInt(e.target.value) || 1 })} className="bg-white border-[#D4AF37]/40 h-11" />
                <div className="flex items-center justify-between text-[#1F1412] font-semibold text-lg pt-2 border-t border-[#D4AF37]/30">
                  <span>Total</span>
                  <span className="font-mono text-[#E65100]">${total}</span>
                </div>
                <Button data-testid="confirm-booking-pay" disabled={paying || !form.traveler_name || !form.traveler_email} onClick={checkout} className="w-full bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-12 text-base">
                  {paying ? "Redirecting to secure checkout…" : `Pay $${total} securely`}
                </Button>
                <p className="text-xs text-[#63534E] text-center">Secured by Stripe · Card test: 4242 4242 4242 4242</p>
              </div>
            </DialogContent>
          </Dialog>
          <p className="text-xs text-[#63534E] mt-5 text-center">
            Prefer email? <a href="/pilgrimage#enquiry" className="text-[#E65100] font-semibold">Send an enquiry</a>
          </p>
        </aside>
      </div>
    </div>
  );
}
